import React, { useState, useEffect, useCallback } from 'react';
import './Mining.css';
import { 
  getUserStats, 
  getTodaysMiningReward,
  checkSyncAvailable,
  getUserActiveSessions,
  getGlobalStats,
  isSmartcoreUser,
  syncFromSmartcore,
  mineTokens,
  claimPending,
  withdrawTokens,
  getTxLink,
  formatCoreValue
} from '../utils/miningContract';
import { 
  getListingProgress, 
  formatCore, 
  formatNumber,
  CORE_TOKEN_ADDRESS,
  MINING_CONTRACT_ADDRESS,
  OPBNB_SCAN_URL
} from '../config/miningConstants';
import { sendTelegramNotification } from '../utils/telegram';

const Mining = ({ account, addToast }) => {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [mining, setMining] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  
  const [userStats, setUserStats] = useState(null);
  const [todaysReward, setTodaysReward] = useState(null);
  const [syncAvailable, setSyncAvailable] = useState(null);
  const [activeSessions, setActiveSessions] = useState([]);
  const [globalStats, setGlobalStats] = useState(null);
  const [smartcoreUser, setSmartcoreUser] = useState({ isUser: false, userId: '0' });
  const [listingProgress, setListingProgress] = useState(getListingProgress());
  const [transactions, setTransactions] = useState([]);
  
  const [timeToUTC, setTimeToUTC] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [isMiningActive, setIsMiningActive] = useState(false);

  const calculateTimeToUTC = useCallback(() => {
    const now = new Date();
    const utcMidnight = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0));
    const diff = utcMidnight - now;
    return {
      hours: Math.floor(diff / (1000 * 60 * 60)),
      minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((diff % (1000 * 60)) / 1000)
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setTimeToUTC(calculateTimeToUTC()), 1000);
    return () => clearInterval(timer);
  }, [calculateTimeToUTC]);

  const loadData = useCallback(async () => {
    if (!account) return;
    setLoading(true);
    try {
      const [userCheck, stats, reward, sync, sessions, global] = await Promise.all([
        isSmartcoreUser(account),
        getUserStats(account),
        getTodaysMiningReward(account),
        checkSyncAvailable(account),
        getUserActiveSessions(account),
        getGlobalStats()
      ]);
      setSmartcoreUser(userCheck);
      setUserStats(stats);
      setTodaysReward(reward);
      setSyncAvailable(sync);
      setActiveSessions(sessions);
      setGlobalStats(global);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [account]);

  useEffect(() => {
    loadData();
    const interval = setInterval(() => setListingProgress(getListingProgress()), 60000);
    return () => clearInterval(interval);
  }, [loadData]);

  const addTransaction = (type, amount, txHash) => {
    setTransactions(prev => [{ id: Date.now(), type, amount, txHash, timestamp: new Date().toLocaleString() }, ...prev].slice(0, 10));
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await syncFromSmartcore();
      if (result.success) {
        addToast('success', `⚡ Synced! +${formatCoreValue(result.newEnergy)} CORE energy`);
        addTransaction('Sync', formatCoreValue(result.newEnergy), result.txHash);
        
        // Send Telegram notification
        sendTelegramNotification('sync', {
          energy: formatCoreValue(result.newEnergy),
          sessions: result.newSessions,
          txHash: result.txHash
        });
        
        await loadData();
      } else {
        addToast('error', `❌ ${result.error}`);
      }
    } catch (error) {
      addToast('error', `❌ ${error.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const handleMine = async () => {
    setMining(true);
    setIsMiningActive(true);
    try {
      const result = await mineTokens();
      if (result.success) {
        addToast('success', `⛏️ Mined ${formatCoreValue(result.amount)} CORE!`);
        addTransaction('Mine', formatCoreValue(result.amount), result.txHash);
        
        // Send Telegram notification
        sendTelegramNotification('mine', {
          amount: formatCoreValue(result.amount),
          day: result.day,
          txHash: result.txHash
        });
        
        await loadData();
        setTimeout(() => setIsMiningActive(false), 5000);
      } else {
        setIsMiningActive(false);
        addToast('error', `❌ ${result.error}`);
      }
    } catch (error) {
      setIsMiningActive(false);
      addToast('error', `❌ ${error.message}`);
    } finally {
      setMining(false);
    }
  };

  const handleClaim = async () => {
    setClaiming(true);
    try {
      const result = await claimPending();
      if (result.success) {
        addToast('success', `✅ Claimed successfully!`);
        addTransaction('Claim', '0', result.txHash);
        
        // Send Telegram notification
        sendTelegramNotification('claim_mining', {
          txHash: result.txHash
        });
        
        await loadData();
      } else {
        addToast('error', `❌ ${result.error}`);
      }
    } catch (error) {
      addToast('error', `❌ ${error.message}`);
    } finally {
      setClaiming(false);
    }
  };

  const handleWithdraw = async () => {
    setWithdrawing(true);
    try {
      const result = await withdrawTokens();
      if (result.success) {
        addToast('success', `💎 Withdrawn ${formatCoreValue(result.amount)} CORE!`);
        addTransaction('Withdraw', formatCoreValue(result.amount), result.txHash);
        
        // Send Telegram notification
        sendTelegramNotification('withdraw', {
          amount: formatCoreValue(result.amount),
          txHash: result.txHash
        });
        
        await loadData();
      } else {
        addToast('error', `❌ ${result.error}`);
      }
    } catch (error) {
      addToast('error', `❌ ${error.message}`);
    } finally {
      setWithdrawing(false);
    }
  };

  const copyAddress = (text) => {
    navigator.clipboard.writeText(text);
    addToast('success', '📋 Copied!');
  };

  // Calculate user's total energy from sessions
  const userTotalEnergy = activeSessions.reduce((sum, s) => sum + parseFloat(s.dailyReward || 0), 0);
  const userAvailableEnergy = activeSessions.filter(s => parseInt(s.daysRemaining) > 0).reduce((sum, s) => sum + parseFloat(s.dailyReward || 0), 0);

  const canMine = userStats?.canMineToday && parseFloat(todaysReward?.reward || 0) > 0;
  const hasPending = parseFloat(userStats?.pendingBalance || 0) > 0;
  const hasWithdrawable = parseFloat(userStats?.withdrawableBalance || 0) > 0;

  if (!account) {
    return (
      <div className="mining-page">
        <div className="mining-bg">
          <div className="bg-grid"></div>
          <div className="bg-aurora"></div>
          <div className="bg-stars">{[...Array(50)].map((_, i) => <span key={i} style={{ '--i': i }}></span>)}</div>
        </div>
        <div className="mining-hero">
          <div className="reactor">
            <div className="reactor-base"></div>
            <div className="reactor-ring r1"><div className="ring-segment"></div></div>
            <div className="reactor-ring r2"><div className="ring-segment"></div></div>
            <div className="reactor-ring r3"><div className="ring-segment"></div></div>
            <div className="reactor-core"><img src="/core.png" alt="CORE" /></div>
            <div className="energy-field">{[...Array(8)].map((_, i) => <div key={i} className="energy-ball" style={{ '--i': i }}></div>)}</div>
            <div className="hologram-rings">{[...Array(3)].map((_, i) => <div key={i} className="holo-ring" style={{ '--i': i }}></div>)}</div>
          </div>
          <h1 className="hero-title">CORE<span>MINING</span></h1>
          <p className="hero-sub">Connect wallet to initialize reactor</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mining-page">
      {/* Background */}
      <div className="mining-bg">
        <div className="bg-grid"></div>
        <div className="bg-aurora"></div>
        <div className="bg-stars">{[...Array(50)].map((_, i) => <span key={i} style={{ '--i': i }}></span>)}</div>
        <div className="bg-scan"></div>
      </div>

      {/* Hero Reactor */}
      <div className={`mining-hero ${isMiningActive ? 'mining-active' : ''}`}>
        <div className={`reactor ${canMine ? 'ready' : ''} ${isMiningActive ? 'active' : ''}`}>
          <div className="reactor-base"></div>
          <div className="reactor-ring r1"><div className="ring-segment"></div><div className="ring-dot"></div></div>
          <div className="reactor-ring r2"><div className="ring-segment"></div><div className="ring-dot"></div></div>
          <div className="reactor-ring r3"><div className="ring-segment"></div><div className="ring-dot"></div></div>
          <div className="reactor-core"><img src="/core.png" alt="CORE" /></div>
          <div className="energy-field">
            {[...Array(12)].map((_, i) => <div key={i} className="energy-ball" style={{ '--i': i }}></div>)}
          </div>
          <div className="hologram-rings">
            {[...Array(4)].map((_, i) => <div key={i} className="holo-ring" style={{ '--i': i }}></div>)}
          </div>
          {isMiningActive && (
            <div className="mining-fx">
              <div className="pulse-wave"></div>
              <div className="pulse-wave delay"></div>
              {[...Array(16)].map((_, i) => <div key={i} className="spark" style={{ '--i': i }}></div>)}
            </div>
          )}
        </div>

        {/* Status */}
        {canMine && (
          <div className="status-card ready">
            <div className="status-glow"></div>
            <span className="status-tag">REACTOR READY</span>
            <div className="status-amount">
              <span className="amount-value">{formatCoreValue(todaysReward?.reward, 2)}</span>
              <span className="amount-unit">CORE</span>
            </div>
          </div>
        )}

        {hasPending && !canMine && (
          <div className="status-card pending">
            <div className="status-glow"></div>
            <span className="status-tag">PROCESSING</span>
            <div className="status-amount">
              <span className="amount-value">{formatCoreValue(userStats?.pendingBalance, 2)}</span>
              <span className="amount-unit">CORE</span>
            </div>
            <div className="countdown-box">
              <span className="countdown-label">AVAILABLE IN</span>
              <div className="countdown-display">
                <div className="time-unit"><span>{String(timeToUTC.hours).padStart(2, '0')}</span><small>HRS</small></div>
                <div className="time-sep">:</div>
                <div className="time-unit"><span>{String(timeToUTC.minutes).padStart(2, '0')}</span><small>MIN</small></div>
                <div className="time-sep">:</div>
                <div className="time-unit"><span>{String(timeToUTC.seconds).padStart(2, '0')}</span><small>SEC</small></div>
              </div>
            </div>
          </div>
        )}

        {!canMine && !hasPending && userStats?.totalEnergy > 0 && (
          <div className="status-card complete">
            <span className="status-icon">✓</span>
            <span>MINING COMPLETE TODAY</span>
          </div>
        )}
      </div>

      {/* Alert */}
      {!smartcoreUser.isUser && (
        <div className="alert-box">
          <span className="alert-icon">⚠</span>
          <div>
            <strong>Not Registered</strong>
            <p>Join Smartcore to start mining CORE tokens</p>
          </div>
        </div>
      )}

      {/* Contract */}
      <div className="contract-box" onClick={() => copyAddress(CORE_TOKEN_ADDRESS)}>
        <span className="contract-label">CORE TOKEN</span>
        <code>{CORE_TOKEN_ADDRESS.slice(0, 10)}...{CORE_TOKEN_ADDRESS.slice(-8)}</code>
        <a href={`${OPBNB_SCAN_URL}/token/${CORE_TOKEN_ADDRESS}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
          opBNBscan ↗
        </a>
      </div>

      {/* Action Buttons */}
      <div className="actions">
        {syncAvailable?.hasNewInvestments && (
          <button className="action-btn sync" onClick={handleSync} disabled={syncing}>
            <span className="btn-glow"></span>
            {syncing ? <><span className="spinner"></span>SYNCING...</> : <><span className="icon">⚡</span>SYNC ENERGY<span className="badge">+{formatCore(syncAvailable.totalNewEnergy)}</span></>}
          </button>
        )}

        {canMine && (
          <button className="action-btn mine" onClick={handleMine} disabled={mining}>
            <span className="btn-glow"></span>
            {mining ? <><span className="spinner"></span>MINING...</> : <><span className="icon">⛏</span>MINE NOW</>}
          </button>
        )}

        {hasPending && !canMine && (
          <button className="action-btn processing" disabled>
            <span className="icon">⏳</span>MINING...<span className="badge">{formatCoreValue(userStats?.pendingBalance, 2)}</span>
          </button>
        )}

        {hasWithdrawable && (
          <>
            <button className="action-btn claim" onClick={handleClaim} disabled={claiming}>
              <span className="btn-glow"></span>
              {claiming ? <><span className="spinner"></span>CLAIMING...</> : <><span className="icon">✓</span>CLAIM CORE</>}
            </button>
            <button className="action-btn withdraw" onClick={handleWithdraw} disabled={withdrawing}>
              <span className="btn-glow"></span>
              {withdrawing ? <><span className="spinner"></span>WITHDRAWING...</> : <><span className="icon">💎</span>WITHDRAW<span className="badge">{formatCoreValue(userStats?.withdrawableBalance, 2)}</span></>}
            </button>
          </>
        )}

        {!canMine && !hasPending && !hasWithdrawable && userStats?.totalEnergy > 0 && (
          <button className="action-btn disabled" disabled>
            <span className="icon">◷</span>COME BACK TOMORROW
          </button>
        )}
      </div>

      {/* Global Stats */}
      <div className="panel global-stats">
        <div className="panel-header">
          <span className="panel-icon">🌐</span>
          <h3>GLOBAL NETWORK</h3>
          <span className="live-badge">● LIVE</span>
        </div>
        <div className="stats-grid g2x2">
          <div className="stat-card">
            <div className="stat-visual">
              <svg viewBox="0 0 36 36" className="stat-ring">
                <circle cx="18" cy="18" r="16" className="ring-bg"/>
                <circle cx="18" cy="18" r="16" className="ring-fill" style={{ strokeDashoffset: 0 }}/>
              </svg>
              <span className="stat-emoji">👥</span>
            </div>
            <span className="stat-value">{formatNumber((parseInt(globalStats?.totalMiners || 0) + 998))}</span>
            <span className="stat-label">Total Miners</span>
          </div>
          <div className="stat-card">
            <div className="stat-visual">
              <svg viewBox="0 0 36 36" className="stat-ring">
                <circle cx="18" cy="18" r="16" className="ring-bg"/>
                <circle cx="18" cy="18" r="16" className="ring-fill green" style={{ strokeDashoffset: 0 }}/>
              </svg>
              <span className="stat-emoji">⚡</span>
            </div>
            <span className="stat-value">{formatCore(globalStats?.totalEnergyDistributed || '0')}</span>
            <span className="stat-label">Energy Distributed</span>
          </div>
          
          
        </div>
      </div>

      {/* User Stats */}
      <div className="panel user-stats">
        <div className="panel-header">
          <span className="panel-icon">👤</span>
          <h3>YOUR MINING STATUS</h3>
        </div>
        <div className="user-stats-grid">
          <div className="user-stat-card">
            <div className="usc-icon-wrap">
              <span className="usc-icon">🔋</span>
            </div>
            <div className="usc-data">
              <span className="usc-value">{formatCore(userStats?.totalEnergy || '0')}</span>
              <span className="usc-label">Total Energy</span>
            </div>
          </div>
          <div className="user-stat-card">
            <div className="usc-icon-wrap green">
              <span className="usc-icon">⚡</span>
            </div>
            <div className="usc-data">
              <span className="usc-value">{formatCore(userStats?.availableEnergy || '0')}</span>
              <span className="usc-label">Available</span>
            </div>
          </div>
          <div className="user-stat-card">
            <div className="usc-icon-wrap cyan">
              <span className="usc-icon">💎</span>
            </div>
            <div className="usc-data">
              <span className="usc-value">{formatCore(userStats?.totalMined || '0')}</span>
              <span className="usc-label">Mined</span>
            </div>
          </div>
          <div className="user-stat-card mining">
            <div className="usc-icon-wrap gold">
              <span className="usc-icon">⏳</span>
            </div>
            <div className="usc-data">
              <span className="usc-value">{formatCore(userStats?.pendingBalance || '0')}</span>
              <span className="usc-label">Mining</span>
            </div>
          </div>
          <div className="user-stat-card claimed">
            <div className="usc-icon-wrap success">
              <span className="usc-icon">✓</span>
            </div>
            <div className="usc-data">
              <span className="usc-value">{formatCore(userStats?.totalWithdrawn || '0')}</span>
              <span className="usc-label">Claimed</span>
            </div>
          </div>
        </div>
      </div>

      {/* Active Sessions */}
      {activeSessions.length > 0 && (
        <div className="panel sessions">
          <div className="panel-header">
            <span className="panel-icon">⚙️</span>
            <h3>ACTIVE SESSIONS</h3>
            <span className="count-badge">{activeSessions.length}</span>
          </div>
          <div className="sessions-list">
            {activeSessions.map((session) => {
              const totalDays = parseInt(session.dailyReward) > 0 ? Math.round(parseFloat(session.dailyReward) / 1e17) : 100;
              const daysLeft = parseInt(session.daysRemaining);
              const progress = totalDays > 0 ? ((totalDays - daysLeft) / totalDays) * 100 : 0;
              
              return (
                <div key={session.id} className="session-card">
                  <div className="session-top">
                    <span className="session-id">SESSION #{session.id}</span>
                    <span className="session-status">● ACTIVE</span>
                  </div>
                  <div className="session-data">
                    <div className="session-item">
                      <span className="item-label">Daily Yield</span>
                      <span className="item-value">{formatCoreValue(session.dailyReward, 2)} CORE</span>
                    </div>
                    <div className="session-item">
                      <span className="item-label">Days Left</span>
                      <span className="item-value">{daysLeft}</span>
                    </div>
                  </div>
                  <div className="session-progress">
                    <div className="progress-track">
                      <div className="progress-fill" style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}></div>
                    </div>
                    <span className="progress-text">{progress.toFixed(0)}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Reports */}
      <div className="panel reports">
        <div className="panel-header">
          <span className="panel-icon">📊</span>
          <h3>REPORTS</h3>
        </div>
        <div className="reports-grid">
          <a href={`${OPBNB_SCAN_URL}/address/${account}`} target="_blank" rel="noopener noreferrer" className="report-link">
            <span>👤</span>Your Transactions<span className="arrow">↗</span>
          </a>
          <a href={`${OPBNB_SCAN_URL}/token/${CORE_TOKEN_ADDRESS}`} target="_blank" rel="noopener noreferrer" className="report-link">
            <span>🪙</span>CORE Token<span className="arrow">↗</span>
          </a>
          <a href={`${OPBNB_SCAN_URL}/address/${MINING_CONTRACT_ADDRESS}`} target="_blank" rel="noopener noreferrer" className="report-link">
            <span>⛏️</span>Mining Contract<span className="arrow">↗</span>
          </a>
          <a href={`${OPBNB_SCAN_URL}/token/${CORE_TOKEN_ADDRESS}?a=${account}`} target="_blank" rel="noopener noreferrer" className="report-link">
            <span>💎</span>Your Balance<span className="arrow">↗</span>
          </a>
        </div>
        {transactions.length > 0 && (
          <div className="recent-tx">
            <h4>Recent Activity</h4>
            {transactions.map((tx) => (
              <a key={tx.id} href={getTxLink(tx.txHash)} target="_blank" rel="noopener noreferrer" className="tx-row">
                <span className="tx-type">{tx.type}</span>
                <span className="tx-amount">{tx.amount} CORE</span>
                <span className="tx-hash">{tx.txHash.slice(0, 8)}...{tx.txHash.slice(-6)}</span>
                <span className="tx-arrow">↗</span>
              </a>
            ))}
          </div>
        )}
      </div>

      {/* Listing */}
      <div className="panel listing">
        <div className="panel-header">
          <span className="panel-icon">🚀</span>
          <h3>EXCHANGE LISTING</h3>
        </div>
        <div className="listing-content">
          <div className="listing-bar">
            <div className="bar-track">
              <div className="bar-fill" style={{ width: `${Math.max(1, parseFloat(listingProgress.percentage))}%` }}></div>
            </div>
            <div className="bar-marks">
              <span>DEX</span><span>T2</span><span>T1</span><span>🚀</span>
            </div>
          </div>
          <div className="listing-percent">{listingProgress.percentage}%</div>
        </div>
      </div>

      {/* How It Works */}
      <div className="panel info">
        <div className="panel-header">
          <span className="panel-icon">ℹ️</span>
          <h3>HOW IT WORKS</h3>
        </div>
        <div className="info-steps">
          <div className="step"><span className="step-num">1</span><div><strong>Sync Energy</strong><p>Investments generate mining energy</p></div></div>
          <div className="step"><span className="step-num">2</span><div><strong>Mine Daily</strong><p>Click Mine once per UTC day</p></div></div>
          <div className="step"><span className="step-num">3</span><div><strong>Claim & Withdraw</strong><p>After UTC 00:00, claim and withdraw</p></div></div>
        </div>
        <div className="info-warn">⚠ Missing a day = tokens lost forever!</div>
      </div>
    </div>
  );
};

export default Mining;
